import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase, getCurrentUser } from "@/lib/supabaseClient";
import { Save, ArrowLeft, Eye, EyeOff, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { SVGMapViewer } from "@/components/SVGMapViewer";
import { StormTypeLegend } from "@/components/StormTypeBadge";
import { LightningActivityIndex } from "@/components/LightningActivityIndex";
import { ThreatBreakdown } from "@/components/ThreatIndicators";
import {
  SNOWFALL_TOTALS_OPTIONS,
  SEVERE_PHENOMENA_OPTIONS,
  SnowfallTotals,
  SeverePhenomena,
} from "@/components/SnowfallInfo";

const STORM_TYPE_OPTIONS = [
  { value: "thundery_showers", label: "Thundery Showers", color: "#E6D8AD" },
  { value: "weak", label: "Weak", color: "#A8CFB6" },
  { value: "moderate", label: "Moderate", color: "#E9D85A" },
  { value: "strong", label: "Strong", color: "#F89C27" },
  { value: "severe", label: "Severe", color: "#F33D14" },
  { value: "violent", label: "Violent", color: "#B45AA9" },
];

const THREAT_TYPES = [
  { key: "hail", label: "Hail" },
  { key: "wind", label: "Wind" },
  { key: "flooding", label: "Flooding" },
  { key: "mcs", label: "MCS" },
  { key: "mesocyclonic", label: "Tornado" },
  { key: "supercells", label: "Supercells" },
];

const EMPTY_FORM = {
  outlook_date: new Date().toISOString().split("T")[0],
  valid_from: "",
  valid_to: "",
  title: "",
  headline: "",
  description: "",
  discussion: "",
  type: "thunderstorm",
  storm_types: [],
  lightning_activity: 0,
  hail: false,
  wind: false,
  flooding: false,
  mcs: false,
  mesocyclonic: false,
  supercells: false,
  hail_risk: 0,
  wind_risk: 0,
  flooding_risk: 0,
  mcs_risk: 0,
  mesocyclonic_risk: 0,
  supercells_risk: 0,
  snowfall_totals: [],
  severe_phenomena: [],
  svg_content: "",
  is_active: false,
  is_published: false,
};

export default function AdminEditOutlook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const svgFileRef = useRef(null);

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    getCurrentUser().then((u) => {
      if (!u) navigate("/admin/login");
    });
    if (isEdit) fetchOutlook();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchOutlook = async () => {
    try {
      const { data, error } = await supabase
        .from("outlooks")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      setForm({
        outlook_date: data.outlook_date || new Date().toISOString().split("T")[0],
        valid_from: data.valid_from ? data.valid_from.slice(0, 16) : "", // truncate to "YYYY-MM-DDTHH:mm" for datetime-local input
        valid_to: data.valid_to ? data.valid_to.slice(0, 16) : "",
        title: data.title || "",
        headline: data.headline || "",
        description: data.description || "",
        discussion: data.discussion || "",
        type: data.type || "thunderstorm",
        storm_types: data.storm_types || [],
        lightning_activity: data.lightning_activity ?? 0,
        hail: data.hail || false,
        wind: data.wind || false,
        flooding: data.flooding || false,
        mcs: data.mcs || false,
        mesocyclonic: data.mesocyclonic || false,
        supercells: data.supercells || false,
        hail_risk: data.hail_risk || 0,
        wind_risk: data.wind_risk || 0,
        flooding_risk: data.flooding_risk || 0,
        mcs_risk: data.mcs_risk || 0,
        mesocyclonic_risk: data.mesocyclonic_risk || 0,
        supercells_risk: data.supercells_risk || 0,
        snowfall_totals: data.snowfall_totals || [],
        severe_phenomena: data.severe_phenomena || [],
        svg_content: data.svg_content || "",
        is_active: data.is_active || false,
        is_published: data.is_published || false,
      });
    } catch (err) {
      toast.error("Failed to load outlook");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  };

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleStormType = (type) => {
    setForm((prev) => ({
      ...prev,
      storm_types: prev.storm_types.includes(type)
        ? prev.storm_types.filter((t) => t !== type)
        : [...prev.storm_types, type],
    }));
  };

  const toggleArrayItem = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }));
  };

  const handleSVGUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".svg") && file.type !== "image/svg+xml") {
      toast.error("Please upload an SVG file");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      set("svg_content", ev.target.result);
      toast.success("SVG loaded");
    };
    reader.readAsText(file);
  };

  const handleSave = async (publish = null) => {
    if (!form.title) {
      toast.error("Title is required");
      return;
    }

    if (form.valid_from && form.valid_to && new Date(form.valid_from) >= new Date(form.valid_to)) {
      toast.error("Valid From must be earlier than Valid To");
      return;
    }

    setSaving(true);
    try {
      // If marking active, deactivate all others first
      if (form.is_active) {
        let deactivateQuery = supabase
          .from("outlooks")
          .update({ is_active: false })
          .eq("is_active", true);
        // When editing, exclude the current outlook from the deactivation
        if (isEdit && id) {
          deactivateQuery = deactivateQuery.neq("id", id);
        }
        await deactivateQuery;
      }

      const payload = {
        ...form,
        valid_from: form.valid_from === "" ? null : form.valid_from,
        valid_to: form.valid_to === "" ? null : form.valid_to,
        is_published: publish !== null ? publish : form.is_published,
        updated_at: new Date().toISOString(),
      };

      if (isEdit) {
        const { error } = await supabase.from("outlooks").update(payload).eq("id", id);
        if (error) throw error;
        toast.success("Outlook updated");
      } else {
        const { error } = await supabase.from("outlooks").insert(payload);
        if (error) throw error;
        toast.success("Outlook created");
      }
      navigate("/admin");
    } catch (err) {
      toast.error(err.message || "Failed to save outlook");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1220] flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-[#FBBF24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1220]" data-testid="admin-edit-page">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/5 bg-[#111827]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Link to="/admin" className="p-2 hover:bg-[#0B1220] transition-colors">
                <ArrowLeft className="w-5 h-5 text-[#9CA3AF]" />
              </Link>
              <span className="font-heading text-sm font-bold tracking-wider text-[#F9FAFB] uppercase">
                {isEdit ? "Edit Outlook" : "New Outlook"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPreview((v) => !v)}
                className="border-white/10 rounded-none text-xs uppercase tracking-wider"
                data-testid="preview-btn"
              >
                {showPreview ? <EyeOff className="w-4 h-4 mr-1.5" /> : <Eye className="w-4 h-4 mr-1.5" />}
                {showPreview ? "Hide Preview" : "Preview"}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSave(false)}
                disabled={saving}
                className="border-white/10 rounded-none text-xs uppercase tracking-wider"
                data-testid="save-draft-btn"
              >
                Save Draft
              </Button>
              <Button
                size="sm"
                onClick={() => handleSave(true)}
                disabled={saving}
                className="bg-[#FBBF24] text-[#0B1220] hover:bg-[#F59E0B] font-bold uppercase tracking-wider rounded-none"
                data-testid="publish-btn"
              >
                {saving ? (
                  <div className="w-4 h-4 border-2 border-[#0B1220] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1.5" />
                    Publish
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Form */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
              <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280] mb-4">
                Basic Information
              </h2>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Outlook Type *</Label>
                <div className="flex gap-2">
                  {[
                    { value: "thunderstorm", label: "⚡ Thunderstorm" },
                    { value: "snowfall", label: "❄ Snowfall" },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set("type", opt.value)}
                      className="flex-1 py-2 px-3 border text-xs uppercase tracking-wider font-medium transition-all"
                      style={{
                        borderColor: form.type === opt.value ? "#FBBF24" : "rgba(255,255,255,0.1)",
                        color: form.type === opt.value ? "#FBBF24" : "#9CA3AF",
                        background: form.type === opt.value ? "#FBBF2415" : "transparent",
                      }}
                      data-testid={`type-btn-${opt.value}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Outlook Date *</Label>
                <Input
                  type="date"
                  value={form.outlook_date}
                  onChange={(e) => set("outlook_date", e.target.value)}
                  className="bg-[#0B1220] border-white/10 rounded-none text-sm"
                  data-testid="date-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Valid From (UTC)</Label>
                  <Input
                    type="datetime-local"
                    value={form.valid_from}
                    onChange={(e) => set("valid_from", e.target.value)}
                    className="bg-[#0B1220] border-white/10 rounded-none text-sm"
                    data-testid="valid-from-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Valid To (UTC)</Label>
                  <Input
                    type="datetime-local"
                    value={form.valid_to}
                    onChange={(e) => set("valid_to", e.target.value)}
                    className="bg-[#0B1220] border-white/10 rounded-none text-sm"
                    data-testid="valid-to-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Title *</Label>
                <Input
                  type="text"
                  value={form.title}
                  onChange={(e) => set("title", e.target.value)}
                  className="bg-[#0B1220] border-white/10 rounded-none text-sm"
                  placeholder="e.g. Severe Thunderstorm Outlook"
                  data-testid="title-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Headline</Label>
                <Input
                  type="text"
                  value={form.headline}
                  onChange={(e) => set("headline", e.target.value)}
                  className="bg-[#0B1220] border-white/10 rounded-none text-sm"
                  placeholder="e.g. ISOLATED SEVERE THUNDERSTORMS POSSIBLE ACROSS WALES"
                  data-testid="headline-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Overview / Description</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  className="bg-[#0B1220] border-white/10 rounded-none text-sm min-h-[80px]"
                  placeholder="Brief overview of the outlook..."
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#6B7280]">Forecaster Discussion</Label>
                <Textarea
                  value={form.discussion}
                  onChange={(e) => set("discussion", e.target.value)}
                  className="bg-[#0B1220] border-white/10 rounded-none text-sm min-h-[140px]"
                  placeholder="Full technical discussion text..."
                  data-testid="discussion-input"
                />
              </div>
            </div>

            {/* SVG Map */}
            <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
              <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                SVG Map
              </h2>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => svgFileRef.current?.click()}
                  className="border-white/10 rounded-none text-xs uppercase tracking-wider"
                  data-testid="upload-svg-btn"
                >
                  <Upload className="w-4 h-4 mr-1.5" />
                  Upload SVG File
                </Button>
                {form.svg_content && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => set("svg_content", "")}
                    className="text-[#9CA3AF] hover:text-[#F9FAFB] text-xs uppercase tracking-wider"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>

              <input
                ref={svgFileRef}
                type="file"
                accept=".svg,image/svg+xml"
                className="hidden"
                onChange={handleSVGUpload}
                data-testid="svg-file-input"
              />

              <Textarea
                value={form.svg_content}
                onChange={(e) => set("svg_content", e.target.value)}
                className="bg-[#0B1220] border-white/10 rounded-none text-xs font-mono min-h-[100px]"
                placeholder="Paste SVG content here, or upload an SVG file above..."
                data-testid="svg-content-input"
              />
            </div>

            {/* Thunderstorm-specific fields */}
            {form.type === "thunderstorm" && (
              <>
                {/* Thunderstorm Types */}
                <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
                  <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                    Thunderstorm Types
                  </h2>
                  <div className="grid grid-cols-2 gap-2">
                    {STORM_TYPE_OPTIONS.map((opt) => {
                      const selected = form.storm_types.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleStormType(opt.value)}
                          className="flex items-center gap-2 p-3 border text-xs uppercase tracking-wider font-medium transition-all text-left"
                          style={{
                            borderColor: selected ? opt.color : "rgba(255,255,255,0.1)",
                            color: selected ? opt.color : "#9CA3AF",
                            background: selected ? `${opt.color}15` : "transparent",
                          }}
                          data-testid={`storm-type-${opt.value}`}
                        >
                          <span
                            className="w-3 h-3 flex-shrink-0"
                            style={{ backgroundColor: selected ? opt.color : "rgba(255,255,255,0.2)" }}
                          />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Lightning Activity Index */}
                <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
                  <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                    Lightning Activity Index
                  </h2>
                  <LightningActivityIndex value={form.lightning_activity} />
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="1"
                      value={form.lightning_activity}
                      onChange={(e) => set("lightning_activity", parseInt(e.target.value, 10))}
                      className="flex-1 accent-[#FBBF24]"
                      data-testid="lai-slider"
                    />
                    <span className="font-mono text-sm text-[#F9FAFB] w-8 text-center">
                      {form.lightning_activity}
                    </span>
                  </div>
                </div>

                {/* Threat Breakdown */}
                <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
                  <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                    Threat Breakdown
                  </h2>
                  <div className="space-y-3">
                    {THREAT_TYPES.map(({ key, label }) => {
                      const active = form[key];
                      const riskKey = `${key}_risk`;
                      const risk = form[riskKey];
                      return (
                        <div key={key} className="flex items-center gap-3">
                          {/* Toggle */}
                          <button
                            type="button"
                            onClick={() => set(key, !active)}
                            className="flex items-center gap-2 w-32 flex-shrink-0 text-xs uppercase tracking-wider font-medium transition-all"
                            style={{ color: active ? "#FBBF24" : "#6B7280" }}
                            data-testid={`threat-toggle-${key}`}
                          >
                            <span
                              className="w-4 h-4 border flex items-center justify-center flex-shrink-0"
                              style={{
                                borderColor: active ? "#FBBF24" : "rgba(255,255,255,0.15)",
                                backgroundColor: active ? "#FBBF2420" : "transparent",
                              }}
                            >
                              {active && <span className="w-2 h-2 bg-[#FBBF24]" />}
                            </span>
                            {label}
                          </button>
                          {/* % Risk slider */}
                          <input
                            type="range"
                            min="0"
                            max="100"
                            step="5"
                            value={risk}
                            disabled={!active}
                            onChange={(e) => set(riskKey, parseInt(e.target.value, 10))}
                            className="flex-1 accent-[#FBBF24] disabled:opacity-30"
                            data-testid={`threat-risk-${key}`}
                          />
                          <span
                            className="font-mono text-xs w-10 text-right"
                            style={{ color: active ? "#F9FAFB" : "#6B7280" }}
                          >
                            {risk}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Snowfall-specific fields */}
            {form.type === "snowfall" && (
              <>
                {/* Snowfall Totals */}
                <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
                  <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                    Snowfall Totals
                  </h2>
                  <div className="grid grid-cols-2 gap-2">
                    {SNOWFALL_TOTALS_OPTIONS.map((opt) => {
                      const selected = form.snowfall_totals.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleArrayItem("snowfall_totals", opt.value)}
                          className="flex items-center gap-2 p-3 border text-xs uppercase tracking-wider font-medium transition-all text-left"
                          style={{
                            borderColor: selected ? opt.color : "rgba(255,255,255,0.1)",
                            color: selected ? opt.color : "#9CA3AF",
                            background: selected ? `${opt.color}15` : "transparent",
                          }}
                          data-testid={`snowfall-total-${opt.value}`}
                        >
                          <span
                            className="w-3 h-3 flex-shrink-0"
                            style={{ backgroundColor: selected ? opt.color : "rgba(255,255,255,0.2)" }}
                          />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Severe Phenomena */}
                <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
                  <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                    Severe Phenomena
                  </h2>
                  <div className="grid grid-cols-1 gap-2">
                    {SEVERE_PHENOMENA_OPTIONS.map((opt) => {
                      const selected = form.severe_phenomena.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleArrayItem("severe_phenomena", opt.value)}
                          className="flex items-center gap-2 p-3 border text-xs uppercase tracking-wider font-medium transition-all text-left"
                          style={{
                            borderColor: selected ? opt.color : "rgba(255,255,255,0.1)",
                            color: selected ? opt.color : "#9CA3AF",
                            background: selected ? `${opt.color}15` : "transparent",
                          }}
                          data-testid={`severe-phenomena-${opt.value}`}
                        >
                          <span
                            className="w-3 h-3 flex-shrink-0"
                            style={{ backgroundColor: selected ? opt.color : "rgba(255,255,255,0.2)" }}
                          />
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            {/* Publishing Options */}
            <div className="bg-[#111827] border border-white/10 p-6 space-y-4">
              <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                Publishing
              </h2>

              {/* Active outlook toggle */}
              <button
                type="button"
                onClick={() => set("is_active", !form.is_active)}
                className="flex items-center gap-3 w-full text-left"
                data-testid="active-toggle"
              >
                <span
                  className="w-10 h-5 border relative flex items-center transition-all"
                  style={{
                    borderColor: form.is_active ? "#22C55E" : "rgba(255,255,255,0.15)",
                    backgroundColor: form.is_active ? "#22C55E20" : "transparent",
                  }}
                >
                  <span
                    className="absolute w-3 h-3 transition-all"
                    style={{
                      backgroundColor: form.is_active ? "#22C55E" : "#6B7280",
                      left: form.is_active ? "calc(100% - 14px)" : "2px",
                    }}
                  />
                </span>
                <div>
                  <p className="text-sm text-[#F9FAFB] font-medium">Mark as Active Outlook</p>
                  <p className="text-[10px] text-[#6B7280] mt-0.5">
                    Only one outlook can be active at a time. This will display on the homepage.
                  </p>
                </div>
              </button>
            </div>
          </div>

          {/* Preview Panel */}
          {showPreview && (
            <div className="space-y-4" data-testid="preview-panel">
              <h2 className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">
                Preview
              </h2>

              <SVGMapViewer svgContent={form.svg_content} />

              {form.type === "thunderstorm" && (
                <>
                  {form.storm_types.length > 0 && (
                    <div className="bg-[#0B1220] border border-white/10 p-4 space-y-2">
                      <p className="font-heading text-xs uppercase tracking-widest text-[#6B7280]">Thunderstorm Types</p>
                      <StormTypeLegend types={form.storm_types} />
                    </div>
                  )}
                  <div className="bg-[#0B1220] border border-white/10 p-4">
                    <LightningActivityIndex value={form.lightning_activity} />
                  </div>
                  <div className="bg-[#0B1220] border border-white/10 p-4">
                    <ThreatBreakdown outlook={form} />
                  </div>
                </>
              )}

              {form.type === "snowfall" && (
                <>
                  {form.severe_phenomena.length > 0 && (
                    <div className="bg-[#0B1220] border border-white/10 p-4">
                      <SeverePhenomena phenomena={form.severe_phenomena} />
                    </div>
                  )}
                  {form.snowfall_totals.length > 0 && (
                    <div className="bg-[#0B1220] border border-white/10 p-4">
                      <SnowfallTotals totals={form.snowfall_totals} />
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
